import _ from 'lodash';
import { Fragment, useEffect, useState, useMemo } from 'react';
import { Button, Stack, Typography, useTheme } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';

import { Page } from '../theme/elements/Page';
import { FormCard, FormCardSingleColumnLayout } from 'theme/elements/Forms';
import { FormButton } from 'theme/elements/Buttons';

import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';

import { selectTranslations } from 'state/reducers/translation.reducers';
import { getCompanyDashboard } from 'state/reducers/customer-accounts.reducers';
import {
	getOptionsDependenciesForCreate,
	getCustomDependenciesForCreate,
	getInitialFormValuesForCreate,
	getCurrencies,
} from 'state/reducers/global.reducer';
import { getUserDetails } from 'state/reducers/user.reducers';

import { createFeature } from 'services/pending-requests.service';

import { pendingRequestsTypesForms } from 'sections/pendingRequests/forms.types';
import {
	getCompanyFeatureCreatePayload,
	getInitialFormValues,
	addCompanyBalanceValidation,
} from 'sections/pendingRequests/helper';
import { overrideFormsToCreate } from 'sections/pendingRequests/forms.types.create';

import FormCreatorComponent, { isValidForm } from 'components/FormCreatorComponent';
import { formatNumber } from 'components/FormCreatorComponent/helpers';
import { validateFields } from 'components/FormCreatorComponent/validation';
import { createFormProps, fieldType, validationTypes } from 'components/FormCreatorComponent/types';

import { pendingRequestsTypes } from 'constants/requests.constants';
import { pagesEnum, timing } from 'constants/ui.constants';
import { mapingFeatureToEndpoint } from 'constants/pending-requests.constants';
import { getHelperText, getValidation } from 'sections/NewAccounts/Add/Stepper/helper';
import { newAccountsSteps } from 'constants/new_accounts.constants';
import { setSuccessMessage } from 'state/reducers/ui.reducers';
import { validatePerType } from 'sections/CustomerAccounts/helper';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { createRefund, getHouseAccountBalances } from 'services/balances.service';
import { getHouseBalances } from 'state/reducers/balance.reducer';
import { PopupComponent } from 'components/PopupComponent';
import { ButtonComponent } from 'components/ButtonComponent';
import { getError as getBalanceErrorMessage, setError as setBalanceError } from 'state/reducers/balance.reducer';
import { WarningDialog } from 'components/WarningDialog';

export const CompanyFeatureCreateApp = () => {
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const navigate = useNavigate();
	const [initialValidation, setInitialValidation] = useState<Record<string, any>>({});
	const [formValues, setFormValues] = useState<Record<string, any>>({});
	const [forms, setForms] = useState<createFormProps[]>();
	const [formState, setFormState] = useState<Record<string, any>>({});
	const [inProgress, setInProgress] = useState<boolean>(false);
	const [completeCreation, setCompleteCreation] = useState<boolean>(false);
	const company = useAppSelector(getCompanyDashboard)?.basicData;
	const companyData = useAppSelector(getCompanyDashboard);
	const { permissions } = useAppSelector(getUserDetails);
	const { type } = useParams();
	const optionsDependencies: any = useAppSelector(getOptionsDependenciesForCreate(type as pendingRequestsTypes));
	const customDependencies: any = useAppSelector(getCustomDependenciesForCreate(type as pendingRequestsTypes));
	const initialFormValues: any = useAppSelector(getInitialFormValuesForCreate(type as pendingRequestsTypes));
	const createRefundPermission = hasPermission([permissionEnums.BO_SIGNER_CREATE_REFUND]);
	const houseBalances = useAppSelector(getHouseBalances);
	const [openRefundPopup, setOpenRefundPopup] = useState(false);
	const theme = useTheme();
	const currencies = useAppSelector(getCurrencies);
	const [isApproveButtonAvailable, setIsApproveButtonAvailable] = useState<boolean>(false);
	const balanceErrorMessage = useAppSelector(getBalanceErrorMessage);
	const currenciesList = useAppSelector(getCurrencies);
	const [coinWithDecimalNotAllowedChosen, setCoinWithDecimalNotAllowedChosen] = useState<boolean>(false);
	const [isRequireValidate, setIsRequireValidate] = useState<boolean>(false);

	useEffect(() => {
		if (_.isNil(company)) {
			navigate(`/${pagesEnum.CUSTOMER_ACCOUNTS}`);
		}
	}, [company]);

	useEffect(() => {
		dispatch(getHouseAccountBalances());
	}, []);

	useEffect(() => {
		if (openRefundPopup) {
			const timer = setTimeout(() => {
				setIsApproveButtonAvailable(true);
			}, 3000);

			return () => clearTimeout(timer);
		}
	}, [openRefundPopup]);

	useEffect(() => {
		if (type && _.has(pendingRequestsTypesForms, type)) {
			const basicForms = _.get(pendingRequestsTypesForms, type as string);
			const initialValues = {};
			basicForms.map((form) => {
				form.fieldsGroups.map((group) => {
					group.fields.map((field) => {
						_.assign(initialValues, {
							[field.props.name]: initialFormValues ? initialFormValues[field.props.name] || '' : '',
						});
					});
				});
			});
			setFormValues(initialValues);
		}
	}, [type]);

	const refundCompanyName = useMemo(() => {
		return company?.hebrewName ?? company?.name;
	}, [company]);

	useEffect(() => {
		if (type && _.has(pendingRequestsTypesForms, type)) {
			const basicForms = _.get(pendingRequestsTypesForms, type as string);
			const override = _.get(overrideFormsToCreate, type as string);
			const final = _.merge({}, basicForms, override);
			const formsToShow = _.filter(final, (form) => !form.hide);
			const _forms = _.map(formsToShow, (form) => ({
				...form,
				name: type === pendingRequestsTypes.REFUNDS ? refundCompanyName : form.name,
				formName: form.name,
				fieldsGroups: _.map(form.fieldsGroups, (group) => ({
					...group,
					fields: _.map(group.fields, (field) => ({
						...field,
						props: {
							...field.props,
							helperText: getHelperText(company, newAccountsSteps.USER_DETAILS, field, t),
							options:
								_.isEqual(field.type, fieldType.select) && _.has(optionsDependencies, field.props.name)
									? _.get(optionsDependencies, field.props.name)
									: field.props?.options || [],
							...([pendingRequestsTypes.REFUNDS, pendingRequestsTypes.BALANCES].includes(
								type as pendingRequestsTypes
							) && ['currencyId', 'currency.id'].includes(field.props.name)
								? {
										options:
											houseBalances?.map((item: any) => ({
												value: item.currency.id,
												label: item.currency.name,
											})) ?? [],
										disabled: false,
										required: true,
								  }
								: {}),
							customData:
								_.isEqual(field.type, fieldType.custom) && _.has(customDependencies, field.props.name)
									? _.get(customDependencies, field.props.name)
									: field.props?.customData || [],
						},
						validation: addCompanyBalanceValidation(
							getValidation(
								company,
								newAccountsSteps.USER_DETAILS,
								field,
								coinWithDecimalNotAllowedChosen
							),
							field,
							type,
							formState,
							formsToShow,
							companyData
						),
					})),
				})),
			}));

			const isRequireReValidate =
				coinWithDecimalNotAllowedChosen &&
				[pendingRequestsTypes.REFUNDS, pendingRequestsTypes.BALANCES].includes(type as pendingRequestsTypes);
			setIsRequireValidate(isRequireReValidate);
			setForms(_forms);
		}
	}, [houseBalances, coinWithDecimalNotAllowedChosen, formState]);

	useEffect(() => {
		if (formValues) {
			const typeForms = _.get(pendingRequestsTypesForms, type as string).filter((form: any) => !form.hide);
			let formsState: Record<string, any> = {};
			typeForms.forEach((form: any) => {
				const dirtyValues =
					_.has(formState, form.name) && _.has(formState[form.name], 'dirtyFields')
						? _.get(formState[form.name], 'dirtyFields')
						: {};
				formsState = {
					...formsState,
					[form.name]: { fieldsGroups: form.fieldsGroups, values: formValues, dirtyValues },
				};
			});
			setFormState(formsState);
		}
	}, [formValues]);

	const handleSubmit = () => {
		let values = formValues;
		_.values(formState).forEach(
			(prop) =>
				(values =
					prop?.dirtyFields && !_.isEmpty(prop.dirtyFields) ? { ...values, ...prop.dirtyFields } : values)
		);

		let invalidsFields = {};
		if (forms) {
			forms.map((form, index: number) => {
				form.fieldsGroups.map((group, index2: number) => {
					const invalids = validateFields(group.fields, values);
					const validate = validatePerType(values, type as pendingRequestsTypes, companyData, t);
					invalidsFields = { ...invalidsFields, ...invalids, ...validate };
				});
			});
		}
		//if form invalid show errors
		if (!_.isEmpty(invalidsFields)) {
			setInitialValidation(invalidsFields);
			window.scrollTo(0, 0);
			return;
		}

		const currencyId = Number(values.currencyId) || Number(values?.['currency.id']) || Number(values?.currency?.id);

		const payload: Record<string, any> = {
			...formValues,
			...values,
			companyId: company?.id,
		};

		if (currencyId != null && !isNaN(currencyId)) {
			payload.currencyId = currencyId;
		}

		const props = getCompanyFeatureCreatePayload(type || '', payload);

		if (props.name !== '') {
			setInProgress(true);
			dispatch(createFeature(props))
				.unwrap()
				.then((res) => {
					if (res?.success) {
						dispatch(setSuccessMessage(true));
						setCompleteCreation(true);
						navigate(-1);
					} else {
						const final = getInitialFormValues(type || '', {
							...formValues,
							...values,
						});
						setFormValues(final);
					}
				})
				.catch((rejectedValueOrSerializedError) => {
					console.log(rejectedValueOrSerializedError);
				})
				.finally(() => setInProgress(false));
		}
	};

	const hasApprovePermission = useMemo(() => {
		if (permissions.permissions) {
			const permission = 'approve_' + _.get(mapingFeatureToEndpoint, type || '');
			return permissions.permissions.includes(permission);
		}
	}, [permissions]);

	useEffect(() => {
		if (permissions.permissions) {
			const permission = 'read_' + _.get(mapingFeatureToEndpoint, type || '');

			if (permissions.permissions.includes(permission) && !createRefundPermission) {
				navigate('/' + pagesEnum.PAGE_NOT_FOUND);
			}
		}
	}, [permissions]);

	const handleAddRefundButton = async () => {
		const refund = {
			currencyId: formState[refundCompanyName]?.values?.currencyId,
			amount: formState[refundCompanyName]?.values?.amount,
			refundReason: formState[refundCompanyName]?.values?.boNotes,
			companyId: company.id,
		};

		try {
			setOpenRefundPopup(false);
			await dispatch(createRefund(refund));
			setFormValues({});
			setFormState({});
			setForms([]);
		} catch (error) {
			console.error('Refund creation failed:', error);
		}
	};

	const basicButtonStyle = { borderRadius: '8px', width: '320px', height: '48px' };

	const openDialog = useMemo(() => {
		return balanceErrorMessage.exist;
	}, [balanceErrorMessage]);

	const handleChange = (formName: string, newFormState: Record<string, any>) => {
		if (
			(type === pendingRequestsTypes.REFUNDS || type === pendingRequestsTypes.BALANCES) &&
			newFormState?.values?.currencyId
		) {
			const selectedCurrency = currenciesList.find((c: any) => c.id == newFormState?.values?.currencyId);
			if (!selectedCurrency?.decimalPlaces) {
				setCoinWithDecimalNotAllowedChosen(true);
			} else {
				setCoinWithDecimalNotAllowedChosen(false);
			}
		}

		// For WITHDRAWAL_SUPPORT: trigger re-validation when currency changes and amount exists
		if (
			type === pendingRequestsTypes.WITHDRAWAL_SUPPORT &&
			formName === 'page.pendingRequests.balance.field.transferDetails' &&
			newFormState?.values?.currency &&
			newFormState?.values?.amount
		) {
			// Update the initial validation to trigger re-validation
			const amountFieldName = 'amount';
			const currentAmount = newFormState.values.amount;

			// Find the balance for the selected currency (company balance, not house balance)
			const selectedBalance = companyData?.balances?.find(
				(balance: any) => balance.currencyId === Number(newFormState.values.currency)
			);

			const updatedFormState = { ...newFormState };

			if (selectedBalance && Number(currentAmount) > selectedBalance.amount) {
				setInitialValidation((prev) => ({
					...prev,
					[amountFieldName]: {
						name: amountFieldName,
						valid: false,
						errorText: 'validations.errors.exceedsBalance',
					},
				}));
				// Update form validity to false since amount exceeds balance
				updatedFormState.isValid = false;
			} else {
				setInitialValidation((prev) => {
					const newValidation = { ...prev };
					delete newValidation[amountFieldName];
					return newValidation;
				});
				// Recalculate form validity - if no other errors, it should be valid
				// Check if there are any other validation errors
				const hasOtherErrors = Object.keys(newFormState.invalids || {}).some(
					(key) => key !== amountFieldName && !newFormState.invalids[key].valid
				);
				updatedFormState.isValid = !hasOtherErrors;
			}

			setFormState((prevState) => ({ ...prevState, [formName]: updatedFormState }));
		} else {
			setFormState((prevState) => ({ ...prevState, [formName]: newFormState }));
		}
	};

	return (
		forms && (
			<Fragment>
				<Page includeGoBack loading={inProgress} title={t('page.pendingRequests.table.' + type + '.title')}>
					<FormCardSingleColumnLayout>
						<>
							<WarningDialog
								open={openDialog}
								subTitle={balanceErrorMessage?.text}
								handleCancel={() => {
									dispatch(setBalanceError({ exist: false }));
								}}
							/>
							{forms &&
								forms.map((form, index: number) => {
									return (
										<FormCard key={index} title={t(form.name)}>
											<FormCreatorComponent
												{...form}
												translation={t}
												values={formValues}
												initialValidation={initialValidation}
												checkInitValidation={false}
												handleChange={(newFormState: any) =>
													handleChange(form.name, newFormState)
												}
												isRequireValidate={isRequireValidate}
											/>
										</FormCard>
									);
								})}
						</>
						<Stack direction="row" justifyContent="space-between" width="100%">
							{type !== pendingRequestsTypes.REFUNDS && (
								<FormButton>
									<Button
										onClick={() => {
											navigate(-1);
										}}
										variant="outlined"
									>
										{t('button.cancel')}
									</Button>
								</FormButton>
							)}
							{hasApprovePermission && (
								<FormButton>
									<Stack direction="row" gap={2}>
										<Button
											variant="contained"
											onClick={handleSubmit}
											disabled={
												completeCreation ||
												inProgress ||
												!_.every(_.values(formState), { isValid: true })
											}
										>
											{t('button.ok')}
										</Button>
									</Stack>
								</FormButton>
							)}
							{type === pendingRequestsTypes.REFUNDS && (
								<FormButton>
									<Stack direction="row" gap={2}>
										<Button
											variant="contained"
											onClick={() => {
												setOpenRefundPopup(true);
											}}
											disabled={!formState[refundCompanyName]?.isValid}
										>
											{t('page.customerAccounts.forms.balance_refund.create_button')}
										</Button>
									</Stack>
								</FormButton>
							)}
							{
								<PopupComponent
									open={openRefundPopup}
									title={t('page.customerAccounts.forms.balance_refund.create_refund_popup_title')}
									subtitle={t('page.customerAccounts.forms.balance_refund.create_refund_popup_text')}
									isDefaultButton={false}
								>
									<Typography variant="h4" sx={{ fontWeight: 700 }}>
										{currencies[formState[refundCompanyName]?.values?.currencyId - 1]?.symbol}
										{formatNumber(formState[refundCompanyName]?.dirtyFields?.amount)}
									</Typography>
									<ButtonComponent
										variantType={'contained'}
										disabled={!isApproveButtonAvailable || !company}
										handleClick={() => {
											handleAddRefundButton();
										}}
										text={t(
											'page.customerAccounts.forms.balance_refund.create_refund_popup_approve'
										)}
										styleObj={{ ...basicButtonStyle }}
									/>
									<ButtonComponent
										variantType={'text'}
										disabled={false}
										handleClick={() => {
											setOpenRefundPopup(false);
											setIsApproveButtonAvailable(false);
										}}
										text={`${t(
											'page.customerAccounts.forms.balance_refund.create_refund_popup_deny'
										)}`}
										styleObj={{
											...basicButtonStyle,
											'&:hover': {
												background: theme.palette.root.basicDeeoGreen,
												boxShadow: 'none',
												color: 'white',
											},
										}}
									/>
								</PopupComponent>
							}
						</Stack>
					</FormCardSingleColumnLayout>
				</Page>
			</Fragment>
		)
	);
};
